<template>
  <div class="space-y-1">
    <!-- Label -->
    <label 
      v-if="label"
      :for="fieldId"
      :class="labelClasses"
    >
      {{ label }}
      <span v-if="required" class="text-red-500 ml-1">*</span>
    </label>

    <!-- Conteneur du champ -->
    <div class="relative">
      <!-- Icône gauche -->
      <div 
        v-if="leftIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <component 
          :is="leftIcon" 
          :class="leftIconClasses"
        />
      </div>

      <!-- Champ de saisie -->
      <input
        :id="fieldId"
        :value="modelValue"
        :type="type"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :class="inputClasses"
        :aria-invalid="hasError"
        :aria-describedby="hasError ? `${fieldId}-error` : hasHelpText ? `${fieldId}-help` : undefined"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />

      <!-- Icône droite -->
      <div 
        v-if="rightIcon"
        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
      >
        <component 
          :is="rightIcon" 
          :class="rightIconClasses"
        />
      </div>
    </div>

    <!-- Texte d'aide -->
    <p 
      v-if="hasHelpText && !hasError"
      :id="`${fieldId}-help`"
      class="text-sm text-gray-600"
    >
      {{ helpText }}
    </p>

    <!-- Message d'erreur -->
    <p 
      v-if="hasError"
      :id="`${fieldId}-error`"
      class="text-sm text-red-600"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

export interface InputFieldProps {
  modelValue: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
  name?: string;
  label?: string;
  placeholder?: string;
  helpText?: string;
  errorMessage?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  autocomplete?: string;
  leftIcon?: unknown;
  rightIcon?: unknown;
  size?: 'sm' | 'md' | 'lg';
}

export interface InputFieldEmits {
  (e: 'update:modelValue', value: string): void;
  (e: 'blur', event: FocusEvent): void;
  (e: 'focus', event: FocusEvent): void;
}

const props = withDefaults(defineProps<InputFieldProps>(), {
  type: 'text',
  size: 'md',
  modelValue: '',
  name: undefined,
  label: undefined,
  placeholder: undefined,
  helpText: undefined,
  errorMessage: undefined,
  required: false,
  disabled: false,
  readonly: false,
  autocomplete: undefined,
  leftIcon: undefined,
  rightIcon: undefined,
});

const emit = defineEmits<InputFieldEmits>();

// État local
const isFocused = ref(false);
const fieldId = computed(() => props.name || `input-field-${Math.random().toString(36).substr(2, 9)}`);

// États computés
const hasError = computed(() => !!props.errorMessage);
const hasHelpText = computed(() => !!props.helpText);

// Classes computées
const labelClasses = computed(() => {
  const baseClasses = 'block text-sm font-medium';
  
  if (hasError.value) {
    return `${baseClasses} text-red-700`;
  }
  
  return `${baseClasses} text-gray-700`;
});

const inputClasses = computed(() => {
  const baseClasses = 'block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors';
  
  // Tailles
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2',
    lg: 'px-4 py-3 text-lg',
  };
  
  // Padding pour les icônes
  let paddingClasses = sizeClasses[props.size];
  if (props.leftIcon) {
    paddingClasses = paddingClasses.replace('px-3', 'pl-10 pr-3');
  }
  if (props.rightIcon) {
    paddingClasses = paddingClasses.replace('pr-3', 'pr-10');
  }
  if (props.leftIcon && props.rightIcon) {
    paddingClasses = paddingClasses.replace('px-3', 'pl-10 pr-10');
  }
  
  // États
  let stateClasses = '';
  if (hasError.value) {
    stateClasses = 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500';
  } else if (isFocused.value) {
    stateClasses = 'border-blue-300 ring-1 ring-blue-500';
  }
  
  if (props.disabled) {
    stateClasses += ' bg-gray-50 text-gray-500 cursor-not-allowed';
  }
  
  if (props.readonly) {
    stateClasses += ' bg-gray-50';
  }
  
  return [baseClasses, paddingClasses, stateClasses].join(' ');
});

const leftIconClasses = computed(() => {
  const baseClasses = 'w-5 h-5';
  
  if (hasError.value) {
    return `${baseClasses} text-red-400`;
  }
  
  return `${baseClasses} text-gray-400`;
});

const rightIconClasses = computed(() => {
  const baseClasses = 'w-5 h-5';
  
  if (hasError.value) {
    return `${baseClasses} text-red-400`;
  }
  
  return `${baseClasses} text-gray-400`;
});

// Gestionnaires d'événements
const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
};

const handleBlur = (event: FocusEvent): void => {
  isFocused.value = false;
  emit('blur', event);
};

const handleFocus = (event: FocusEvent): void => {
  isFocused.value = true;
  emit('focus', event);
};

// Expose
defineExpose({
  focus: () => {
    const input = document.getElementById(fieldId.value) as HTMLInputElement;
    input?.focus();
  },
  blur: () => {
    const input = document.getElementById(fieldId.value) as HTMLInputElement;
    input?.blur();
  },
});
</script> 